import { useEffect, useState } from 'react';
import { Flex, TextStyled, StyledImg } from '../../component-library';
import { VideoChat } from '../../components';
import { useTestContext } from '../../context/testContext';
import RotatingWheelImgBlack from '../../assets/img/ros_ui_fixationpoint.png';
import RotatingWheelImgWhite from '../../assets/img/ros_ui_fixationpoint_black.png';

let currentDot = null;
let seenDots = [];
let notSeenDots = [];
let notSeenOnceCount = 0;
let totalMisses = 0;
let dotsForDisplay = [];
let randomDotIndex;

let waitForRepeatSeenDotCount = 0;
let totalWaitForRepeatSeenDotCount = 0;
let repeatedSeenDotCount = 5;
let blindSpotSeenCount = 0;
let blindSpotCount = 0;

const currentBoxPosition = {
  animate: false,
  x: 0,
  y: 0,
};

let topPosition = 0;
let leftPosition = 0;

const animationTimeInMiliseconds = 2000;

const transitionCss = `all ${Math.round(animationTimeInMiliseconds / 1000)}s`;

const VisualField = ({ visualFieldTestControl, testNotification }) => {
  const boundaries = {
    x: window.screen.width,
    y: window.screen.height,
  };

  const centerScreen = {
    x: boundaries['x'] / 2,
    y: boundaries['y'] / 2,
  };

  const { testData } = useTestContext();

  const [transformEvent, setTransformEvent] = useState('');

  const [fixationTargetStyle, setFixationTargetStyle] = useState({
    width: 0,
    height: 0,
    zIndex: 9,
    top: 0,
    left: 0,
    position: 'absolute',
    transform: transformEvent,
    transition: transitionCss,
    cursor: 'none',
    boxShadow: 'none',
    margin: 0,
  });

  const thumbnailOperatorStyle = {
    videoBox: {
      position: 'absolute',
      marginTop: 0,
    },
    localParticipant: {
      width: 'auto',
      height: 'auto',
      overflow: 'hidden',
      zIndex: -1,
    },
    remoteParticipant: fixationTargetStyle,
  };

  const [fixationSize, setFixationSize] = useState(null);

  const [totalDots, setTotalDots] = useState(0);
  const [dotSize, setDotSize] = useState(20);
  const [eyeTested, setEyeTested] = useState(null);
  const [blindSpotVanishingXDistancePx, setBlindSpotVanishingXDistancePx] = useState(0);

  const [calculatedLeftPosition, setCalculatedLeftPosition] = useState(centerScreen['x'] - dotSize / 2);
  const [calculatedTopPosition, setCalculatedTopPosition] = useState(centerScreen['x'] - dotSize / 2);
  const [dotVisibility, setDotVisibility] = useState('none');
  const [currentDotDisplayTimeStamp, setCurrentDotDisplayTimeStamp] = useState();
  const [endOfTest, setEndOfTest] = useState(false);
  const [readyToRun, setReadyToRun] = useState(false);

  const [selectedAnimationType, setSelectedAnimationType] = useState('move');
  const [backgroundColor, setBackgroundColor] = useState('white');
  const [stimulusColor, setStimulusColor] = useState('black');
  const [contrastType, setContrastType] = useState(null);
  const [targetDuration, setTargetDuration] = useState(2000);
  const [fixationTargetType, setFixationTargetType] = useState(null);
  const [rotatingWheelImg, setRotatingWheelImg] = useState(null);

  const dotElement = (
    <div
      style={{
        width: dotSize,
        height: dotSize,
        backgroundColor: stimulusColor,
        position: 'absolute',
        borderRadius: dotSize / 2,
        top: calculatedTopPosition,
        left: calculatedLeftPosition,
        display: dotVisibility,
      }}
    ></div>
  );

  const getPixelsForDegree = (degree) => {
    return (degree * blindSpotVanishingXDistancePx) / 15;
  };

  const getCoordinateLimit = (coordinate) => {
    return boundaries[coordinate] - screenLimitBuffer(coordinate) - dotSize;
  };

  const screenLimitBuffer = (coordinate) => {
    return boundaries[coordinate] * testData['visualFieldSettings']['screenBuffer'];
  };

  const getElementsAdjustment = () => {
    return (dotSize / 2) * -1;
  };

  const getFixationTargetReferencePosition = (coordinate) => {
    return currentBoxPosition[coordinate] + fixationSize[coordinate] / 2;
  };

  const processAdjustments = (coordinate, dotCoordinateChangeInPx) => {
    let newAdjustment = 0;
    let moveFixationBox = false;
    let dotPositionAgainstFixation = 0;
    let adjustedPosition = 0;
    let adjustedBoxPosition = 0;

    dotPositionAgainstFixation =
      getFixationTargetReferencePosition(coordinate, dotCoordinateChangeInPx) + dotCoordinateChangeInPx + getElementsAdjustment();

    if (dotPositionAgainstFixation >= screenLimitBuffer(coordinate) && dotPositionAgainstFixation <= getCoordinateLimit(coordinate)) {
      return dotPositionAgainstFixation;
    }

    if (dotPositionAgainstFixation < 0) {
      dotPositionAgainstFixation = dotPositionAgainstFixation * -1;
      newAdjustment = dotPositionAgainstFixation + screenLimitBuffer(coordinate);
      adjustedPosition = screenLimitBuffer(coordinate);
      adjustedBoxPosition = currentBoxPosition[coordinate] + newAdjustment;
      moveFixationBox = true;
    } else if (dotPositionAgainstFixation < screenLimitBuffer(coordinate)) {
      newAdjustment = screenLimitBuffer(coordinate) - dotPositionAgainstFixation;
      adjustedPosition = screenLimitBuffer(coordinate);
      adjustedBoxPosition = currentBoxPosition[coordinate] + newAdjustment;
      moveFixationBox = true;
    } else if (dotPositionAgainstFixation > getCoordinateLimit(coordinate)) {
      newAdjustment = dotPositionAgainstFixation - getCoordinateLimit(coordinate);
      adjustedBoxPosition = currentBoxPosition[coordinate] - newAdjustment;
      adjustedPosition = getCoordinateLimit(coordinate);
      moveFixationBox = true;
    }

    if (moveFixationBox === true) {
      currentBoxPosition['animate'] = true;
      currentBoxPosition[coordinate] = adjustedBoxPosition;
    }

    return adjustedPosition;
  };

  const getPosition = (dotPosition, coordinate, orientation) => {
    if (orientation == 'topLeft') {
      dotPosition = dotPosition * -1;
    }

    if (orientation == 'bottomLeft' && coordinate == 'x') {
      dotPosition = dotPosition * -1;
    }

    if (orientation == 'topRight' && coordinate == 'y') {
      dotPosition = dotPosition * -1;
    }

    const newDotPositionForCoordinate = processAdjustments(coordinate, dotPosition);

    return newDotPositionForCoordinate;
  };

  const expand = () => {
    //setTransformEvent('scale(15)');
  };

  const shrink = () => {
    //setTransformEvent('scale(1)');
  };

  const displayDot = () => {
    const displayTime = getDisplayTime();

    setTimeout(function () {
      setCurrentDotDisplayTimeStamp(new Date().getTime() / 1000);
      setCalculatedTopPosition(topPosition + 'px');
      setCalculatedLeftPosition(leftPosition + 'px');
      setDotVisibility('block');

      setTimeout(function () {
        setDotVisibility('none');
        if (displayTime < 1000) {
          const topupMilisecondsForNextDot = 1000 - displayTime;
          setTimeout(function () {
            nextRandomDot();
          }, topupMilisecondsForNextDot);
        } else {
          nextRandomDot();
        }
      }, displayTime);
    }, getWaitTime());
  };

  const getRepetitionThreshold = () => {
    if (dotsForDisplay.length <= 10) {
      return 3;
    }

    return 5;
  };

  const shouldRepeatSeenDot = () => {
    if (repeatedSeenDotCount > 0) {
      if (seenDots.length == getRepetitionThreshold() && totalWaitForRepeatSeenDotCount == 0) {
        totalWaitForRepeatSeenDotCount = Math.floor(dotsForDisplay.length / getRepetitionThreshold());
        waitForRepeatSeenDotCount = totalWaitForRepeatSeenDotCount;
        return true;
      }

      if (seenDots.length >= 5 && --waitForRepeatSeenDotCount < 0) {
        waitForRepeatSeenDotCount = totalWaitForRepeatSeenDotCount;
        return true;
      }
    }

    return false;
  };

  const getRandomSeenDot = () => {
    let repeatedDot;
    repeatedDot = seenDots[Math.floor(Math.random() * seenDots.length)];
    repeatedDot['repeat'] = true;
    repeatedDot['seentwice'] = false;
    return repeatedDot;
  };

  const isEyeBlindSpot = (currentDot) => {
    if (currentDot['blindspot']) {
      if (eyeTested !== 'bothEyes') {
        if (eyeTested == 'leftEye' && (currentDot['orientation'] == 'topLeft' || currentDot['orientation'] == 'bottomLeft')) {
          return true;
        } else if (eyeTested == 'rightEye' && (currentDot['orientation'] == 'topRight' || currentDot['orientation'] == 'bottomRight')) {
          return true;
        }
      }
    }

    return false;
  };

  const animateToNextDot = (animationType) => {
    switch (animationType) {
      case 'expand':
        setTimeout(function () {
          expand();
          setTimeout(function () {
            shrink();
            updateFixationTargetAttributes({ top: currentBoxPosition['y'], left: currentBoxPosition['x'] });
            displayDot();
          }, animationTimeInMiliseconds * 2);
        }, animationTimeInMiliseconds * 2);
        currentBoxPosition['animate'] = false;
        break;
      case 'move':
        updateFixationTargetAttributes({ top: currentBoxPosition['y'], left: currentBoxPosition['x'] });
        setTimeout(function () {
          displayDot();
        }, animationTimeInMiliseconds);
        break;
    }
  };

  const nextRandomDot = () => {
    if (currentDot) {
      if (!currentDot['seen'] && !isEyeBlindSpot(currentDot)) {
        if (currentDot['notseenonce']) {
          currentDot['notseentwice'] = true;
          notSeenDots.push(currentDot);
        } else {
          currentDot['notseenonce'] = true;
          dotsForDisplay.push(currentDot);
          notSeenOnceCount++;
        }
      } else if (isEyeBlindSpot(currentDot) === true) {
        blindSpotCount++;
        if (--currentDot['repeat'] > 0) {
          dotsForDisplay.push(currentDot);
        }
      } else if (currentDot['seen'] && currentDot['repeat'] === true && !currentDot['seentwice']) {
        repeatedSeenDotCount--;
      }

      updateTestProgress({
        seenDotsCount: seenDots.length,
        notSeenDotsCount: notSeenDots.length,
        totalMissesCount: totalMisses,
        totalDotsCount: totalDots,
        outstandingDotsCount: dotsForDisplay.length,
        activeDot: currentDot,
        blindSpotSeenCount: blindSpotSeenCount,
        repeatedSeenDotCount: repeatedSeenDotCount,
        notSeenOnceCount: notSeenOnceCount,
        blindSpotCount: blindSpotCount,
      });

      currentDot = null;
    }

    if (!dotsForDisplay.length) {
      updateTestProgress({
        seenDotsCount: seenDots.length,
        notSeenDotsCount: notSeenDots.length,
        totalMissesCount: totalMisses,
        totalDotsCount: totalDots,
        outstandingDotsCount: 0,
        blindSpotSeenCount: blindSpotSeenCount,
        repeatedSeenDotCount: repeatedSeenDotCount,
        notSeenOnceCount: notSeenOnceCount,
        endOfTest: true,
        blindSpotCount: blindSpotCount,
      });
      setEndOfTest(true);
      updateFixationTargetAttributes({ zIndex: -1 });
      return false;
    }

    if (shouldRepeatSeenDot()) {
      currentDot = getRandomSeenDot();
    } else if (dotsForDisplay.length && !currentDot) {
      randomDotIndex = Math.floor(Math.random() * dotsForDisplay.length);
      currentDot = dotsForDisplay.splice(randomDotIndex, 1)[0];
    }

    topPosition = getPosition(getPixelsForDegree(currentDot['degreeY']), 'y', currentDot['orientation']);
    leftPosition = getPosition(getPixelsForDegree(currentDot['degreeX']), 'x', currentDot['orientation']);

    if (currentBoxPosition['animate'] === true) {
      animateToNextDot(selectedAnimationType);
    } else {
      displayDot();
    }
  };

  const getMaximumResponseTime = () => {
    if (getDisplayTime() >= 1000) {
      return getDisplayTime();
    }
    return 1000;
  };

  const inResponseWindow = () => {
    const currentTimeStamp = new Date().getTime() / 1000;
    const responseTime = currentTimeStamp - currentDotDisplayTimeStamp;
    if (responseTime <= getMaximumResponseTime()) {
      return true;
    }
    return false;
  };

  const markSeen = () => {
    if (inResponseWindow()) {
      if (currentDot) {
        if (isEyeBlindSpot(currentDot) === true) {
          blindSpotSeenCount++;
          return;
        }

        if (currentDot['seen'] !== true) {
          currentDot['seen'] = true;
          seenDots.push(currentDot);
          if (currentDot.notseenonce) {
            notSeenOnceCount--;
          }
        } else if (currentDot['seen'] === true && currentDot['repeat'] === true) {
          currentDot['seentwice'] = true;
        } else {
          totalMisses++;
        }
      }
    }
  };

  const getWaitTime = () => {
    let miliseconds = Math.floor(Math.random() * 1500);
    let minimumWaitMiliseconds = 800;
    if (getDisplayTime() < 1000) {
      minimumWaitMiliseconds = 300;
    }
    while (miliseconds < minimumWaitMiliseconds) {
      miliseconds = Math.floor(Math.random() * 1500);
    }
    return miliseconds;
  };

  const getDisplayTime = () => {
    return targetDuration;
  };

  const updateTestProgress = (progressData) => {
    testNotification(progressData);
  };

  const clearIntervals = () => {
    const highestId = window.setTimeout(() => {
      for (let i = highestId; i >= 0; i--) {
        window.clearInterval(i);
      }
    }, 0);
  };

  const centraliseDot = () => {
    setCalculatedTopPosition(centerScreen['y'] - visualFieldTestControl['visualFieldTestData']['circleDimensionsPx'] / 2);
    setCalculatedLeftPosition(centerScreen['x'] - visualFieldTestControl['visualFieldTestData']['circleDimensionsPx'] / 2);
  };

  const updateFixationTargetAttributes = (fixationTargetAttributes) => {
    setFixationTargetStyle({
      ...fixationTargetStyle,
      ...fixationTargetAttributes,
    });
  };

  const populateDotsForDisplay = () => {
    dotsForDisplay = [];
    if (visualFieldTestControl['visualFieldTestData']) {
      const visualFieldDots = visualFieldTestControl['visualFieldTestData']['dots'];
      var ibisvisionVisualFieldDotsLength = Object.keys(visualFieldDots).length;

      for (let orientationIndex = 0; orientationIndex < ibisvisionVisualFieldDotsLength; orientationIndex++) {
        for (let dotIndex = 0; dotIndex < visualFieldDots[orientationIndex].length; dotIndex++) {
          visualFieldDots[orientationIndex][dotIndex]['seen'] = false;
          visualFieldDots[orientationIndex][dotIndex]['notseenonce'] = false;
          visualFieldDots[orientationIndex][dotIndex]['notseentwice'] = false;
          if (!visualFieldDots[orientationIndex][dotIndex]['disabled']) {
            dotsForDisplay.push(visualFieldDots[orientationIndex][dotIndex]);
          }
        }
      }
    }
  };

  const resetTestData = () => {
    populateDotsForDisplay();

    seenDots = [];
    notSeenOnceCount = 0;
    notSeenDots = [];
    totalMisses = 0;
    currentDot = null;
    totalWaitForRepeatSeenDotCount = 0;
    waitForRepeatSeenDotCount = 0;
    repeatedSeenDotCount = 5;
    blindSpotSeenCount = 0;
    blindSpotCount = 0;

    currentBoxPosition['y'] = centerScreen['y'] - visualFieldTestControl['visualFieldTestData']['fixationType']['height'] / 2;
    currentBoxPosition['x'] = centerScreen['x'] - visualFieldTestControl['visualFieldTestData']['fixationType']['width'] / 2;

    setDotSize(visualFieldTestControl['visualFieldTestData']['circleDimensionsPx']);
    setBlindSpotVanishingXDistancePx(visualFieldTestControl['visualFieldTestData']['blindSpotVanishingXDistancePx']);
    setEyeTested(visualFieldTestControl['visualFieldTestData']['eyeTested']);
    setContrastType(visualFieldTestControl['visualFieldTestData']['contrastType']);
    setTargetDuration(visualFieldTestControl['visualFieldTestData']['targetDuration']);
    setEndOfTest(false);
    setFixationSize({
      x: visualFieldTestControl['visualFieldTestData']['fixationType']['width'],
      y: visualFieldTestControl['visualFieldTestData']['fixationType']['height'],
    });

    const fixationTargetDefaults = {
      width: visualFieldTestControl['visualFieldTestData']['fixationType']['width'],
      height: visualFieldTestControl['visualFieldTestData']['fixationType']['height'],
      top: currentBoxPosition['y'],
      left: currentBoxPosition['x'],
      zIndex: 9,
    };

    updateFixationTargetAttributes(fixationTargetDefaults);

    centraliseDot();

    setFixationTargetType(visualFieldTestControl['visualFieldTestData']['fixationType']['type']);
  };

  const startTest = () => {
    if (readyToRun === true) {
      nextRandomDot();
    }
  };

  const initColors = () => {
    if (contrastType == 'whiteOnBlack') {
      setBackgroundColor('black');
      setStimulusColor('white');
      setRotatingWheelImg(RotatingWheelImgWhite);
    } else {
      setBackgroundColor('white');
      setStimulusColor('black');
      setRotatingWheelImg(RotatingWheelImgBlack);
    }
  };

  const getFixationTargetComponent = () => {
    switch (fixationTargetType) {
      case 'thumbnailOperator':
        return <VideoChat stacked customStyle={thumbnailOperatorStyle} />;
      case 'rotatingWheel':
        thumbnailOperatorStyle.remoteParticipant = {};
        return (
          <>
            <VideoChat stacked customStyle={thumbnailOperatorStyle} />
            <StyledImg className='rotatingWheelFixation' src={rotatingWheelImg} style={fixationTargetStyle} />
          </>
        );
    }
  };

  useEffect(() => {
    if (visualFieldTestControl['command'] === 'reloadTest') {
      setReadyToRun(false);
      clearIntervals();
      resetTestData();
      setReadyToRun(true);
    } else if (visualFieldTestControl['command'] === 'startVisualField') {
      startTest();
    }
  }, [visualFieldTestControl]);

  useEffect(() => {
    if (contrastType) {
      initColors();
    }
  }, [contrastType]);

  return (
    <Flex onClick={() => markSeen()}>
      {fixationTargetType ? getFixationTargetComponent() : ''}
      <Flex bg={backgroundColor} width='100vw' alignItems='center' height='100vh' justifyContent='center' style={{ zIndex: '8', cursor: 'none' }}>
        {endOfTest ? (
          <TextStyled textAlign='center' fontSize='48px' color={stimulusColor}>
            End of the test.
          </TextStyled>
        ) : (
          dotElement
        )}
      </Flex>
    </Flex>
  );
};

export default VisualField;
